import moment from 'moment';
import { useState } from 'react';
import AutoCompleteSelectModal from '~/components/AutoCompleteSelectModal';
import DatePicker, {
  DATE_PICKER_FORMAT,
  validateDate,
} from '~/components/DatePicker';
import {
  AuditFilters as Filters,
  AuditOption,
  AuditPeriodShortcut,
} from '../models';
import * as St from '../styles';
import SegmentedControl from './SegmentedControl';

const PERIOD_OPTIONS: { id: AuditPeriodShortcut; label: string }[] = [
  { id: '24H', label: 'Últimas 24h' },
  { id: '3D', label: '3 dias' },
  { id: '7D', label: '7 dias' },
  { id: '30D', label: 'Últimos 30 dias' },
];

interface AuditFiltersProps {
  form: Filters;
  period?: AuditPeriodShortcut;
  screens: AuditOption[];
  services: AuditOption[];
  users: AuditOption[];
  markers: AuditOption[];
  loading: boolean;
  /** Busca de usuários por nome no servidor (ms-usuario). */
  onSearchUsers?: (text: string) => Promise<AuditOption[]>;
  onChange: (form: Filters) => void;
  onPeriodChange: (period: AuditPeriodShortcut) => void;
  onClear: () => void;
  onSubmit: () => void;
}

const AuditFilters: React.FC<AuditFiltersProps> = ({
  form,
  period,
  screens,
  services,
  users,
  markers,
  loading,
  onSearchUsers,
  onChange,
  onPeriodChange,
  onClear,
  onSubmit,
}) => {
  const set = (field: keyof Filters) => (value: string) =>
    onChange({ ...form, [field]: value });

  const today = moment().format(DATE_PICKER_FORMAT);
  // A relação entre as datas é validada só na data final, para não acusar erro
  // nos dois campos ao mesmo tempo.
  const startIsValid =
    !!form.dataInicial && !validateDate(form.dataInicial, undefined, today);
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});
  const hasDateError = Object.values(dateErrors).some(Boolean);

  const handleValidate = (field: 'dataInicial' | 'dataFinal') => (
    error?: string,
  ) => setDateErrors((prev) => ({ ...prev, [field]: error }));

  const selectField = (
    label: string,
    field: 'tela' | 'servico' | 'usuario' | 'marcador',
    options: AuditOption[],
    onSearch?: (text: string) => Promise<AuditOption[]>,
  ) => (
    <St.FilterField>
      <AutoCompleteSelectModal
        modal
        onSearch={onSearch}
        searchPlaceholder={onSearch ? 'Buscar por nome' : 'Buscar'}
        label={label}
        name={`audit-${field}`}
        options={options}
        value={form[field]}
        onChange={(option) => set(field)(String(option.id))}
      />
    </St.FilterField>
  );

  return (
    <St.Card>
      <St.CardHeader>
        <St.CardTitle>
          <h2>Explorar atividades</h2>
        </St.CardTitle>
        <SegmentedControl
          options={PERIOD_OPTIONS}
          value={period}
          onChange={onPeriodChange}
        />
      </St.CardHeader>

      <St.FiltersGrid>
        <St.FilterField>
          <DatePicker
            label="Data inicial"
            name="audit-data-inicial"
            value={form.dataInicial}
            maxDate={today}
            required
            onChange={set('dataInicial')}
            onValidate={handleValidate('dataInicial')}
          />
        </St.FilterField>
        <St.FilterField>
          <DatePicker
            label="Data final"
            name="audit-data-final"
            value={form.dataFinal}
            minDate={startIsValid ? form.dataInicial : undefined}
            maxDate={today}
            required
            onChange={set('dataFinal')}
            onValidate={handleValidate('dataFinal')}
          />
        </St.FilterField>
        {selectField('Tela do backoffice', 'tela', screens)}
        {selectField('Serviço', 'servico', services)}
        {selectField('Usuário registrado', 'usuario', users, onSearchUsers)}
        {selectField('Marcador da aplicação', 'marcador', markers)}
      </St.FiltersGrid>

      <St.FiltersActions>
        <St.OutlineButton type="button" onClick={onClear}>
          Limpar
        </St.OutlineButton>
        <St.PrimaryButton
          type="button"
          disabled={loading || hasDateError}
          onClick={onSubmit}
        >
          Consultar
        </St.PrimaryButton>
      </St.FiltersActions>
    </St.Card>
  );
};

export default AuditFilters;
